/**
 * API Client Configuration
 * Provides configured instances of NSwag-generated API clients with MSAL authentication
 */

import { IPublicClientApplication, AccountInfo, SilentRequest } from '@azure/msal-browser';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { Client, IConfig } from '../../generated/api.onboarding.client';
import { apiConfig, getLoginRequest, getAuthProvider, isTestMode } from '../../config';
import { appInsights } from '../../telemetry/appInsights';

/**
 * Creates an IConfig implementation for test mode (no authentication)
 */
export const createTestApiConfig = (): IConfig => {
  return {
    getAuthorization: async (): Promise<string> => {
      // Return a mock token for testing
      return 'test-mock-token';
    },
    AdditionalHeaders: {
      'Content-Type': 'application/json',
    },
  };
};

/**
 * Creates an IConfig implementation that uses MSAL for authentication
 */
export const createApiConfig = (msalInstance: IPublicClientApplication, account: AccountInfo): IConfig => {
  return {
    getAuthorization: async (): Promise<string> => {
      try {
        // Get scopes based on current auth provider (b2c or corporate)
        const authProvider = getAuthProvider();
        const loginReq = getLoginRequest(authProvider);
        const apiScopes = loginReq.scopes && loginReq.scopes.length > 0 ? loginReq.scopes : [];

        if (apiScopes.length === 0) {
          console.warn('No API scopes configured! Using ID token as fallback.');
          const idTokenResponse = await msalInstance.acquireTokenSilent({
            account: account,
            scopes: ['openid', 'profile'],
          });
          return idTokenResponse.idToken;
        }

        const silentRequest: SilentRequest = {
          scopes: apiScopes,
          account: account,
          forceRefresh: false,
        };

        const response = await msalInstance.acquireTokenSilent(silentRequest);

        // Prefer access token, fallback to ID token
        const token = response.accessToken || response.idToken;
        if (!token) {
          throw new Error('No token received from MSAL');
        }

        return token;
      } catch (error) {
        console.error('Failed to acquire token silently:', error);

        // Track token acquisition failure in Application Insights
        const tokenError = error instanceof Error ? error : new Error(String(error));
        appInsights.trackException({
          exception: tokenError,
          severityLevel: SeverityLevel.Warning,
          properties: {
            component: 'apiClient',
            action: 'acquireTokenSilent',
            authProvider: getAuthProvider(),
          },
        });

        // If silent acquisition fails, trigger interactive login with API scopes
        const authProvider = getAuthProvider();
        const loginReq = getLoginRequest(authProvider);
        await msalInstance.acquireTokenRedirect({
          scopes: loginReq.scopes || [],
        });
        throw error;
      }
    },
    AdditionalHeaders: {
      'Content-Type': 'application/json',
    },
  };
};

/**
 * Creates a configured PrehireClient instance
 * In test mode, uses mock config; otherwise uses MSAL authentication
 */
export const createPrehireClient = (
  msalInstance?: IPublicClientApplication | null,
  account?: AccountInfo | null
): Client => {
  if (isTestMode || !msalInstance || !account) {
    const config = createTestApiConfig();
    return new Client(config, apiConfig.baseUrl);
  }
  const config = createApiConfig(msalInstance, account);
  return new Client(config, apiConfig.baseUrl);
};

/**
 * API Client factory - provides all API clients with proper configuration
 */
export class ApiClientFactory {
  private msalInstance: IPublicClientApplication | null;
  private account: AccountInfo | null;
  private config: IConfig;

  constructor(msalInstance?: IPublicClientApplication | null, account?: AccountInfo | null) {
    this.msalInstance = msalInstance ?? null;
    this.account = account ?? null;

    if (isTestMode || !msalInstance || !account) {
      this.config = createTestApiConfig();
    } else {
      this.config = createApiConfig(msalInstance, account);
    }
  }

  get prehire(): Client {
    return new Client(this.config, apiConfig.baseUrl);
  }

  // Add more client getters here as your API grows
  // get anotherClient(): AnotherClient {
  //     return new AnotherClient(this.config, apiConfig.baseUrl);
  // }
}
